import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { Modal, ModalContent, Button, AlertWarningIcon, TextInput, Timing, Theme } from '@spoiler-alert/ui-library';

const useStyles = createUseStyles({
  modal__title: {
    fontSize: '24px',
    padding: 0,
    marginTop: 0,
    height: '25px',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.42,
    letterSpacing: '-0.5px',
    textAlign: 'left',
    marginBottom: '24px',
  },
  modal__body: {
    fontFamily: 'Roboto',
    fontSize: '16px',
    alignSelf: 'stretch',
    lineHeight: 1.5,
    letterSpacing: '-0.18px',
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  footer__container: {
    display: 'flex',
    justifyContent: 'end',
    padding: 24,
    borderTop: `1px solid ${Theme.modalFooterBorderColor}`,
    alignItems: 'center',
  },
  footer__container_space_between: {
    justifyContent: 'space-between',
  },
  modal__cancel: {
    marginRight: 8,
  },
  modal__submit: {
    display: 'flex',
  },
  input__row: {
    padding: '6px 0',
    display: 'flex',
    justifyContent: 'space-between',
  },
  form__container: {
    width: '100%',
  },
  input__container: {
    padding: '0 12px',
    flexGrow: 1,
  },
  row: {
    marginBottom: 24,
  },
  email: {
    width: '48%',
  },
  warningIcon: {
    width: 16,
    height: 16,
    marginRight: 7,
    fill: Theme.red,
  },
  errorMessage: {
    fontSize: 12,
    color: Theme.red,
    display: 'flex',
    justifyContent: 'start',
  },
  contactType: {
    fontSize: 12,
    textTransform: 'uppercase',
    color: Theme.grey,
    marginBottom: 3,
  },
  deleteButton: {
    marginRight: '8px',
    color: Theme.red,
    borderColor: Theme.red,
    '&:hover': {
      backgroundColor: `${Theme.red} !important`,
      color: `${Theme.white} !important`,
      borderColor: `${Theme.red} !important`,
    },
  },
  deleteButtonNoBorder: {
    border: 'none',
    '&:hover': {
      color: `${Theme.red} !important`,
      backgroundColor: `${Theme.white} !important`,
    },
  },
  infoIcon: {
    fill: Theme.red,
    width: 16,
    height: 16,
    marginTop: '3px',
  },
  infoIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginRight: '16px',
  },
  modalInfo: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 4,
    padding: '13px 17px 13px 17px',
    backgroundColor: Theme.red5,
    fontSize: 14,
    marginBottom: 24,
  },
  zeroStateText: {
    fontSize: 16,
    color: Theme.greyDark,
    margin: 'auto',
    width: '228px',
  },
  zeroStateInfo: {
    fontSize: 16,
    color: Theme.greyDark,
    fontWeight: 500,
    marginBottom: 16,
  },
  zeroStateContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  zeroStateIconContainer: {
    display: 'flex',
    margin: 'auto',
  },
  deleteZeroStateText: {
    marginLeft: 12,
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: 10,
    color: Theme.greyDark,
  },
  tableContainer: {
    marginBottom: 12,
  },
});
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const EditContactModal = ({ onHide, open, handleSubmit, loading, email, selectedContactType, deleteContact }) => {
  const classes = useStyles();
  const [emailAddress, setEmailAddress] = useState(email);
  const [emailError, setEmailError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setEmailAddress(email);
  }, [email]);

  const cancel = () => {
    onHide(false);
    setEmailAddress(email);
    setEmailError(false);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const error = await handleSubmit(emailAddress, selectedContactType, email);
    if (error?.code === 'DUPLICATE_EMAIL') {
      setErrorMessage('Duplicate email. Email must be unique within your organization.');
      setEmailError(true);
    }
  };

  const handleChange = (newEmail) => {
    const emailMatch = emailRegex.test(newEmail);
    if (!emailMatch) setErrorMessage('Please enter a valid email address');
    setEmailError(!emailMatch);
    setEmailAddress(newEmail);
  };

  const handleDeleteFromEdit = () => {
    deleteContact();
  };

  return (
    <Modal onHide={onHide} open={open} closeOnEsc closeOnOutsideClick>
      <form onSubmit={submit} className={classes.form__container}>
        <ModalContent>
          <div data-testid="modal-title" className={classes.modal__title}>
            {email === '' ? 'Add Contact' : 'Edit Contact'}
          </div>
          <div data-testid="modal-body" className={classes.modal__body}>
            <div className={`${classes.row} ${classes.email}`}>
              <TextInput
                labelText="Email Address *"
                value={emailAddress}
                onChange={Timing.debounce(handleChange, 500)}
                required
                error={emailError}
              />
              {emailError && (
                <span className={classes.errorMessage}>
                  <AlertWarningIcon className={classes.warningIcon} />
                  {errorMessage}
                </span>
              )}
            </div>
          </div>
        </ModalContent>
        <div className={`${classes.footer__container} ${classes.footer__container_space_between}`}>
          {deleteContact && (
            <div>
              <Button
                className={`${classes.deleteButton} ${classes.deleteButtonNoBorder}`}
                type="button"
                onClick={handleDeleteFromEdit}
                secondary
              >
                Delete Contact
              </Button>
            </div>
          )}
          <div className={classes.modal__submit}>
            <Button
              testId="cancel"
              className={classes.modal__cancel}
              type="button"
              onClick={cancel}
              secondary
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className={classes.actionButton}
              testId="submit"
              type="submit"
              loadingText={'Saving Contact'}
              loading={loading}
              disabled={!emailAddress || emailError || emailAddress === email}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

EditContactModal.propTypes = {
  onHide: PropTypes.func,
  open: PropTypes.bool,
  handleSubmit: PropTypes.func,
  loading: PropTypes.bool,
  email: PropTypes.string,
  selectedContactType: PropTypes.string,
  deleteContact: PropTypes.func,
  setEditContactModalOpen: PropTypes.func,
};

export default EditContactModal;
